import React, { useCallback, useEffect, useRef, useState } from 'react';

import type { IProgressiveStyleSlots } from '../../helpers/applyProgressiveStyleSlots.utils';
import { getProgressivePreviewUrls } from '../../helpers/progressiveStylePreview.utils';
import { removeNeutralDarkBackdrop } from '../../helpers/removeNeutralDarkBackdrop.utils';
import {
  STORE_CHECKOUT_AI_IMAGE_GENERATION_PLACEHOLDER_SRC,
  STORE_CHECKOUT_AI_IMAGE_NO_MORE_REGENERATIONS_TOOLTIP,
  STORE_PROGRESSIVE_STYLE_GENERATE_DELAY_MS,
  STORE_PROGRESSIVE_STYLE_REGENERATE_DELAY_MS,
  type StoreAiPreviewVibe,
} from '../../constants/storeCheckoutAi.constants';
import useViewport from '@/common/context/ViewPort/useViewPort';
import { ToolTip } from '@/components/Tooltip/Tooltip';

import './StoresProgressiveStyleGenerator.scss';

const PSG_ICONS = {
  zoomIn: '/src/assets/svg/store-review/zoom-in.svg',
  refresh: '/src/assets/svg/store-review/refresh-cw.svg',
  chevronLeft: '/src/assets/svg/store-review/chevron-left.svg',
  chevronRight: '/src/assets/svg/store-review/chevron-right.svg',
  close: '/src/assets/svg/store-review/close.svg',
} as const;

const PSG_TOOLTIP_OVERLAY_INNER_STYLE: React.CSSProperties = {
  fontFamily: "'Geist-Regular', 'Geist', sans-serif",
  fontSize: '13px',
  lineHeight: '18px',
  fontWeight: 500,
  color: '#474B6D',
  padding: '4px 8px',
  background: '#fff',
  boxShadow: '0px 4px 15px #8588AC26',
  borderRadius: '2px',
};

type GenPhase = 'idle' | 'generating' | 'generated';

interface IZoomSlide {
  url: string;
  label: string;
  mirrored?: boolean;
}

export interface IStoresProgressiveStyleGeneratorProps {
  layoutMode: 'directPanel';
  presetStyleId: StoreAiPreviewVibe;
  singleImagePanel?: boolean;
  simulateBackSlotFailure?: boolean;
  isReadOnly?: boolean;
  onImagesCommitted: (slots: IProgressiveStyleSlots) => void;
  onDirectPanelDiscarded: () => void;
}

const StoresProgressiveStyleGenerator: React.FC<IStoresProgressiveStyleGeneratorProps> = ({
  layoutMode,
  presetStyleId,
  singleImagePanel = false,
  simulateBackSlotFailure = false,
  isReadOnly = false,
  onImagesCommitted,
  onDirectPanelDiscarded,
}) => {
  const [activeStyleId] = useState<StoreAiPreviewVibe>(presetStyleId);
  const [genPhase, setGenPhase] = useState<GenPhase>('idle');
  const [previewVisible, setPreviewVisible] = useState(false);
  const [frontPreviewUrl, setFrontPreviewUrl] = useState<string | null>(null);
  const [backPreviewUrl, setBackPreviewUrl] = useState<string | null>(null);
  const [designPreviewUrl, setDesignPreviewUrl] = useState<string | null>(null);
  const [frontSlotGenerating, setFrontSlotGenerating] = useState(false);
  const [backSlotGenerating, setBackSlotGenerating] = useState(false);
  const [backSlotFailed, setBackSlotFailed] = useState(false);
  const [frontPreviewVariants, setFrontPreviewVariants] = useState<string[]>([]);
  const [frontPreviewVariantIndex, setFrontPreviewVariantIndex] = useState(0);
  const [frontRegenerateExhausted, setFrontRegenerateExhausted] = useState(false);
  const [showFrontRegenerateLimitMessage, setShowFrontRegenerateLimitMessage] = useState(false);
  const [zoomLightboxOpen, setZoomLightboxOpen] = useState(false);
  const [zoomLightboxSlides, setZoomLightboxSlides] = useState<IZoomSlide[]>([]);
  const [zoomLightboxIndex, setZoomLightboxIndex] = useState(0);

  const generationTokenRef = useRef(0);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const screen = useViewport(true);
  const isMobileViewport = screen === 'mobile';

  const isDirectPanel = layoutMode === 'directPanel';
  const showFrontSlotZoom = !isDirectPanel || singleImagePanel;

  const clearTimers = useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  }, []);

  const scheduleTimer = useCallback((fn: () => void, delay: number) => {
    const id = setTimeout(fn, delay);
    timersRef.current.push(id);
    return id;
  }, []);

  const resetGenerationUi = useCallback(() => {
    clearTimers();
    generationTokenRef.current += 1;
    setGenPhase('idle');
    setPreviewVisible(false);
    setFrontPreviewUrl(null);
    setBackPreviewUrl(null);
    setDesignPreviewUrl(null);
    setFrontSlotGenerating(false);
    setBackSlotGenerating(false);
    setBackSlotFailed(false);
    setFrontPreviewVariants([]);
    setFrontPreviewVariantIndex(0);
    setFrontRegenerateExhausted(false);
    setShowFrontRegenerateLimitMessage(false);
    setZoomLightboxOpen(false);
    setZoomLightboxSlides([]);
    setZoomLightboxIndex(0);
  }, [clearTimers]);

  const displayedFrontPreviewUrl =
    frontPreviewVariants.length > 0
      ? frontPreviewVariants[frontPreviewVariantIndex] ?? null
      : frontPreviewUrl;

  const showThreeSlotPanel =
    previewVisible || genPhase === 'generating' || genPhase === 'generated';

  const showFrontSlotVariantNav =
    frontPreviewVariants.length > 1 && genPhase === 'generated' && !frontSlotGenerating;

  const isDisplayedFrontPreviewMirrored =
    frontPreviewVariants.length > 1 && frontPreviewVariantIndex === 1;

  const showBackSlotFailedState =
    simulateBackSlotFailure && backSlotFailed && genPhase === 'generated' && !backSlotGenerating;

  const isPanelFinalizeDisabled =
    genPhase !== 'generated' ||
    (simulateBackSlotFailure && (backSlotFailed || backSlotGenerating || !backPreviewUrl));

  const isFrontRegenerateDisabled = frontRegenerateExhausted || frontSlotGenerating;

  const frontRegenerateTooltip = frontRegenerateExhausted
    ? STORE_CHECKOUT_AI_IMAGE_NO_MORE_REGENERATIONS_TOOLTIP
    : 'Regenerate';

  const showFrontRegenerateTooltip = !isMobileViewport || !frontRegenerateExhausted;

  const frontSlotLabel = singleImagePanel ? 'Preview' : 'Front';

  const applyKeyedFrontPreviewUrl = useCallback((keyed: string) => {
    setFrontPreviewUrl(keyed);
    setFrontPreviewVariants((variants) => {
      if (variants.length === 0) {
        return [keyed];
      }
      return variants.map(() => keyed);
    });
  }, []);

  const keyAiModelPreviewUrlsIfNeeded = useCallback(
    async (completionToken: number, front: string | null, back: string | null) => {
      if (activeStyleId !== 'ai-model' || completionToken !== generationTokenRef.current) {
        return;
      }

      try {
        if (front && !front.startsWith('data:')) {
          const keyedFront = await removeNeutralDarkBackdrop(front);
          if (completionToken === generationTokenRef.current) {
            applyKeyedFrontPreviewUrl(keyedFront);
          }
        }

        if (back && !back.startsWith('data:')) {
          const keyedBack = await removeNeutralDarkBackdrop(back);
          if (completionToken === generationTokenRef.current) {
            setBackPreviewUrl(keyedBack);
          }
        }
      } catch {
        /* keep original asset URLs */
      }
    },
    [activeStyleId, applyKeyedFrontPreviewUrl]
  );

  const startGeneration = useCallback(() => {
    const urls = getProgressivePreviewUrls(activeStyleId);
    generationTokenRef.current += 1;
    const token = generationTokenRef.current;
    setGenPhase('generating');
    setPreviewVisible(true);
    setFrontPreviewUrl(null);
    setBackPreviewUrl(null);
    setDesignPreviewUrl(null);

    scheduleTimer(() => {
      if (token !== generationTokenRef.current) {
        return;
      }
      setFrontPreviewUrl(urls.front);
      setFrontPreviewVariants([urls.front]);
      setFrontPreviewVariantIndex(0);
      setFrontRegenerateExhausted(false);
      setShowFrontRegenerateLimitMessage(false);
      if (singleImagePanel) {
        setBackPreviewUrl(null);
        setDesignPreviewUrl(null);
        setBackSlotFailed(false);
        keyAiModelPreviewUrlsIfNeeded(token, urls.front, null).catch(() => undefined);
      } else if (simulateBackSlotFailure) {
        setBackPreviewUrl(null);
        setDesignPreviewUrl(urls.design);
        setBackSlotFailed(true);
        keyAiModelPreviewUrlsIfNeeded(token, urls.front, null).catch(() => undefined);
      } else {
        setBackPreviewUrl(urls.back);
        setDesignPreviewUrl(urls.design);
        setBackSlotFailed(false);
        keyAiModelPreviewUrlsIfNeeded(token, urls.front, urls.back).catch(() => undefined);
      }
      setGenPhase('generated');
    }, STORE_PROGRESSIVE_STYLE_GENERATE_DELAY_MS);
  }, [
    activeStyleId,
    keyAiModelPreviewUrlsIfNeeded,
    scheduleTimer,
    simulateBackSlotFailure,
    singleImagePanel,
  ]);

  useEffect(() => {
    startGeneration();
    return () => {
      clearTimers();
      generationTokenRef.current += 1;
    };
  }, [startGeneration, clearTimers]);

  const closeZoomLightbox = useCallback(() => {
    setZoomLightboxOpen(false);
    setZoomLightboxSlides([]);
    setZoomLightboxIndex(0);
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && zoomLightboxOpen) {
        event.preventDefault();
        closeZoomLightbox();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [closeZoomLightbox, zoomLightboxOpen]);

  const openZoomLightbox = (event: React.MouseEvent, fromSlot: 'front' | 'back' | 'design') => {
    event.stopPropagation();
    const slides: IZoomSlide[] = [];

    if (fromSlot === 'front' && frontPreviewVariants.length > 1) {
      frontPreviewVariants.forEach((url, index) => {
        slides.push({ url, label: frontSlotLabel, mirrored: index === 1 });
      });
      setZoomLightboxSlides(slides);
      setZoomLightboxIndex(frontPreviewVariantIndex);
      setZoomLightboxOpen(true);
      return;
    }

    if (displayedFrontPreviewUrl) {
      slides.push({
        url: displayedFrontPreviewUrl,
        label: frontSlotLabel,
        mirrored: isDisplayedFrontPreviewMirrored,
      });
    }
    if (!singleImagePanel && backPreviewUrl) {
      slides.push({ url: backPreviewUrl, label: 'Back' });
    }
    if (!singleImagePanel && designPreviewUrl) {
      slides.push({ url: designPreviewUrl, label: 'Design' });
    }
    if (slides.length === 0) {
      return;
    }

    const slotLabels: Record<'front' | 'back' | 'design', string> = {
      front: frontSlotLabel,
      back: 'Back',
      design: 'Design',
    };
    const want = slotLabels[fromSlot];
    let idx = slides.findIndex((slide) => slide.label === want);
    if (idx < 0) {
      idx = 0;
    }
    setZoomLightboxSlides(slides);
    setZoomLightboxIndex(idx);
    setZoomLightboxOpen(true);
  };

  const runFrontSlotRegenerate = () => {
    if (frontRegenerateExhausted) {
      setShowFrontRegenerateLimitMessage(true);
      return;
    }
    const urls = getProgressivePreviewUrls(activeStyleId);
    generationTokenRef.current += 1;
    const token = generationTokenRef.current;
    setFrontSlotGenerating(true);
    scheduleTimer(() => {
      if (token !== generationTokenRef.current) {
        return;
      }
      setFrontPreviewVariants([urls.front, urls.front]);
      setFrontPreviewVariantIndex(1);
      setFrontRegenerateExhausted(true);
      setFrontPreviewUrl(urls.front);
      setFrontSlotGenerating(false);
      keyAiModelPreviewUrlsIfNeeded(token, urls.front, null).catch(() => undefined);
    }, STORE_PROGRESSIVE_STYLE_REGENERATE_DELAY_MS);
  };

  const onRetryBackSlot = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (isReadOnly || !simulateBackSlotFailure) {
      return;
    }
    setBackSlotGenerating(true);
    generationTokenRef.current += 1;
    const token = generationTokenRef.current;
    scheduleTimer(() => {
      if (token !== generationTokenRef.current) {
        return;
      }
      setBackSlotGenerating(false);
      setBackSlotFailed(true);
      setBackPreviewUrl(null);
    }, STORE_PROGRESSIVE_STYLE_REGENERATE_DELAY_MS);
  };

  const onFinalizePanel = () => {
    if (isReadOnly || isPanelFinalizeDisabled) {
      return;
    }
    const front = displayedFrontPreviewUrl ?? frontPreviewUrl;
    const back = singleImagePanel ? front : backPreviewUrl;
    const design = singleImagePanel ? front : designPreviewUrl;
    if (front && back && design) {
      onImagesCommitted({ front, back, design });
    }
    resetGenerationUi();
  };

  const onDiscardPanel = () => {
    if (isReadOnly) {
      return;
    }
    resetGenerationUi();
    onDirectPanelDiscarded();
  };

  const zoomLightboxCurrent = zoomLightboxSlides[zoomLightboxIndex];

  const renderPsgTooltip = (
    title: string,
    placement: 'top' | 'left' | 'right' | 'bottom',
    child: React.ReactNode
  ) => (
    <ToolTip
      title={title}
      placement={placement}
      arrow
      overlayClassName="stores-psg-tooltip"
      overlayInnerStyle={PSG_TOOLTIP_OVERLAY_INNER_STYLE}
      color="#FFFFFF"
    >
      {child}
    </ToolTip>
  );

  const renderFrontRegenerateControl = (): React.ReactNode => {
    const regenerateButton = (
      <span className="stores-psg__slot-regenerate-wrap">
        <button
          type="button"
          className={[
            'stores-psg__slot-regenerate-btn',
            isFrontRegenerateDisabled ? 'stores-psg__slot-regenerate-btn--disabled' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          aria-label={frontRegenerateTooltip}
          disabled={frontSlotGenerating}
          aria-disabled={isFrontRegenerateDisabled}
          onClick={() => runFrontSlotRegenerate()}
        >
          <img
            src={PSG_ICONS.refresh}
            alt=""
            className="stores-psg__slot-regenerate-btn-icon"
            aria-hidden
          />
        </button>
      </span>
    );

    return showFrontRegenerateTooltip
      ? renderPsgTooltip(frontRegenerateTooltip, 'left', regenerateButton)
      : regenerateButton;
  };

  return (
    <div
      className={[
        'stores-psg',
        isDirectPanel ? 'stores-psg--direct' : '',
        singleImagePanel ? 'stores-psg--single-image' : '',
        simulateBackSlotFailure ? 'stores-psg--back-slot-error' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {showThreeSlotPanel && (
        <div className="stores-psg__panel">
          <div className="stores-psg__panel-slots">
            <div className="stores-psg__slot">
              <div className="stores-psg__slot-head">
                <div className="stores-psg__slot-label stores-psg__slot-label--direct">
                  {frontSlotLabel}
                </div>
              </div>
              <div className="stores-psg__slot-body">
                {(frontSlotGenerating ||
                  (genPhase === 'generating' && !displayedFrontPreviewUrl)) && (
                  <div className="stores-psg__shimmer" />
                )}
                {displayedFrontPreviewUrl && !frontSlotGenerating && (
                  <img
                    src={displayedFrontPreviewUrl}
                    alt={`${frontSlotLabel} preview`}
                    className={[
                      'stores-psg__slot-img',
                      isDisplayedFrontPreviewMirrored ? 'stores-psg__slot-img--mirrored' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  />
                )}
                {showFrontSlotVariantNav && (
                  <div className="stores-psg__slot-variant-nav">
                    <button
                      type="button"
                      className="stores-psg__preview-nav-btn"
                      aria-label="Previous generated front image"
                      onClick={() =>
                        setFrontPreviewVariantIndex(
                          (frontPreviewVariantIndex - 1 + frontPreviewVariants.length) %
                            frontPreviewVariants.length
                        )
                      }
                    >
                      <img
                        src={PSG_ICONS.chevronLeft}
                        alt=""
                        className="stores-psg__preview-nav-icon"
                        aria-hidden
                      />
                    </button>
                    <button
                      type="button"
                      className="stores-psg__preview-nav-btn"
                      aria-label="Next generated front image"
                      onClick={() =>
                        setFrontPreviewVariantIndex(
                          (frontPreviewVariantIndex + 1) % frontPreviewVariants.length
                        )
                      }
                    >
                      <img
                        src={PSG_ICONS.chevronRight}
                        alt=""
                        className="stores-psg__preview-nav-icon"
                        aria-hidden
                      />
                    </button>
                  </div>
                )}
                {genPhase === 'generated' && displayedFrontPreviewUrl && !frontSlotGenerating && (
                  <div className="stores-psg__slot-overlay-actions">
                    {!isReadOnly && renderFrontRegenerateControl()}
                    {showFrontSlotZoom &&
                      renderPsgTooltip(
                        'Zoom',
                        'top',
                        <button
                          type="button"
                          className="stores-psg__slot-zoom-btn"
                          aria-label="Zoom preview"
                          onClick={(event) => openZoomLightbox(event, 'front')}
                        >
                          <img
                            src={PSG_ICONS.zoomIn}
                            alt=""
                            className="stores-psg__slot-zoom-btn-icon"
                            aria-hidden
                          />
                        </button>
                      )}
                  </div>
                )}
              </div>
              {showFrontRegenerateLimitMessage && frontRegenerateExhausted && (
                <p className="stores-psg__slot-regenerate-error" role="alert">
                  {STORE_CHECKOUT_AI_IMAGE_NO_MORE_REGENERATIONS_TOOLTIP}
                </p>
              )}
            </div>

            {!singleImagePanel && (
              <div className="stores-psg__slot">
                <div className="stores-psg__slot-head">
                  <div className="stores-psg__slot-label stores-psg__slot-label--direct">Back</div>
                </div>
                <div className="stores-psg__slot-body">
                  {(backSlotGenerating ||
                    (genPhase === 'generating' && !backPreviewUrl && !showBackSlotFailedState)) && (
                    <div className="stores-psg__shimmer" />
                  )}
                  {showBackSlotFailedState && (
                    <div className="stores-psg__slot-fail">
                      <img
                        className="stores-psg__slot-fail-placeholder"
                        src={STORE_CHECKOUT_AI_IMAGE_GENERATION_PLACEHOLDER_SRC}
                        alt=""
                        aria-hidden
                      />
                      <p className="stores-psg__slot-fail-message">Failed to generate image</p>
                      {!isReadOnly && (
                        <button
                          type="button"
                          className="stores-psg__btn-pink stores-psg__slot-fail-retry"
                          onClick={onRetryBackSlot}
                        >
                          <img
                            src={PSG_ICONS.refresh}
                            alt=""
                            className="stores-psg__slot-fail-retry-icon"
                            aria-hidden
                          />
                          Retry
                        </button>
                      )}
                    </div>
                  )}
                  {backPreviewUrl && !backSlotGenerating && (
                    <img src={backPreviewUrl} alt="Back preview" className="stores-psg__slot-img" />
                  )}
                  {genPhase === 'generated' && backPreviewUrl && !backSlotGenerating && (
                    <div className="stores-psg__slot-overlay-actions">
                      {!isReadOnly &&
                        !isDirectPanel &&
                        renderPsgTooltip(
                          'Regenerate',
                          'top',
                          <button
                            type="button"
                            className="stores-psg__slot-regenerate-btn"
                            aria-label="Regenerate"
                            onClick={(event) => {
                              event.stopPropagation();
                            }}
                          >
                            <img
                              src={PSG_ICONS.refresh}
                              alt=""
                              className="stores-psg__slot-regenerate-btn-icon"
                              aria-hidden
                            />
                          </button>
                        )}
                      {!isDirectPanel &&
                        renderPsgTooltip(
                          'Zoom',
                          'top',
                          <button
                            type="button"
                            className="stores-psg__slot-zoom-btn"
                            aria-label="Zoom preview"
                            onClick={(event) => openZoomLightbox(event, 'back')}
                          >
                            <img
                              src={PSG_ICONS.zoomIn}
                              alt=""
                              className="stores-psg__slot-zoom-btn-icon"
                              aria-hidden
                            />
                          </button>
                        )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {!isReadOnly && (
            <div className="stores-psg__panel-footer">
              <button type="button" className="stores-psg__btn-cancel" onClick={onDiscardPanel}>
                Cancel
              </button>
              <button
                type="button"
                className="stores-psg__btn-pink"
                disabled={isPanelFinalizeDisabled}
                onClick={onFinalizePanel}
              >
                {(() => {
                  if (genPhase === 'generating') {
                    return 'Generating…';
                  }
                  if (singleImagePanel) {
                    return 'Use this Image';
                  }
                  return 'Use These Images';
                })()}
              </button>
            </div>
          )}
        </div>
      )}

      {zoomLightboxOpen && zoomLightboxCurrent && (
        <div
          className="stores-psg__lightbox"
          role="dialog"
          aria-modal
          aria-label={`Enlarged preview: ${zoomLightboxCurrent.label}`}
          onClick={closeZoomLightbox}
        >
          <div className="stores-psg__lightbox-panel" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="stores-psg__lightbox-close"
              aria-label="Close preview"
              onClick={closeZoomLightbox}
            >
              <img
                src={PSG_ICONS.close}
                alt=""
                className="stores-psg__lightbox-close-icon"
                aria-hidden
              />
            </button>
            <div className="stores-psg__lightbox-stage">
              {zoomLightboxSlides.length > 1 && (
                <button
                  type="button"
                  className="stores-psg__lightbox-nav stores-psg__lightbox-nav--prev"
                  aria-label="Previous image"
                  onClick={(event) => {
                    event.stopPropagation();
                    setZoomLightboxIndex(
                      (zoomLightboxIndex - 1 + zoomLightboxSlides.length) %
                        zoomLightboxSlides.length
                    );
                  }}
                >
                  <img
                    src={PSG_ICONS.chevronLeft}
                    alt=""
                    className="stores-psg__lightbox-nav-icon"
                    aria-hidden
                  />
                </button>
              )}
              <img
                src={zoomLightboxCurrent.url}
                alt={`${zoomLightboxCurrent.label} preview (enlarged)`}
                className={[
                  'stores-psg__lightbox-img',
                  zoomLightboxCurrent.mirrored ? 'stores-psg__lightbox-img--mirrored' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              />
              {zoomLightboxSlides.length > 1 && (
                <button
                  type="button"
                  className="stores-psg__lightbox-nav stores-psg__lightbox-nav--next"
                  aria-label="Next image"
                  onClick={(event) => {
                    event.stopPropagation();
                    setZoomLightboxIndex((zoomLightboxIndex + 1) % zoomLightboxSlides.length);
                  }}
                >
                  <img
                    src={PSG_ICONS.chevronRight}
                    alt=""
                    className="stores-psg__lightbox-nav-icon"
                    aria-hidden
                  />
                </button>
              )}
            </div>
            <div className="stores-psg__lightbox-caption">{zoomLightboxCurrent.label}</div>
          </div>
        </div>
      )}
    </div>
  );
};

StoresProgressiveStyleGenerator.displayName = 'StoresProgressiveStyleGenerator';

export default StoresProgressiveStyleGenerator;
